// src/HomePage.jsx
console.log("🟠 Home rendered");

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import LoginModal from "./components/LoginModal";
import RegisterModal from "./components/RegisterModal";
import { useAuth } from "./lib/AuthContext";
import { motion } from "framer-motion";
import "./HomePage.css";

import Mission from "./components/Mission.jsx";
import Offers from "./components/Offer.jsx";
import Why from "./components/WhySection.jsx";
import Feedback from "./components/FeedbackSection";
import Footer from "./components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 1 } },
};

const HomePage = ({ openLoginModalOnLoad }) => {
  useEffect(() => {
    if (openLoginModalOnLoad) {
      openLoginModal();
    }
  }, [openLoginModalOnLoad]);

  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setRegisterModalOpen] = useState(false);
  const [initialRole, setInitialRole] = useState("client");
  const { user } = useAuth();
  const navigate = useNavigate();

  const openLoginModal = () => setLoginModalOpen(true);
  const closeLoginModal = () => setLoginModalOpen(false);

  const openRegisterModal = (role = "client") => {
    setInitialRole(role);
    setRegisterModalOpen(true);
  };
  const closeRegisterModal = () => setRegisterModalOpen(false);

  const switchToRegister = () => {
    closeLoginModal();
    openRegisterModal();
  };

  const switchToLogin = () => {
    closeRegisterModal();
    openLoginModal();
  };

  const handleBookNowClick = () => {
    if (user) {
      if (user.role === "expert" || user.role === "therapist") {
        navigate("/expert/calendar");
      } else if (user.role === "client") {
        navigate("/client/find-therapist");
      }
    } else {
      openLoginModal();
    }
  };

  return (
    <div className="homepage">

      <Header onLoginClick={openLoginModal} onRegisterClick={openRegisterModal} />

      {/* HERO SECTION */}
      <motion.section
        id="home"
        className="hero-section"
        initial="hidden"
        animate="show"
        variants={fadeIn}
      >
        <div className="hero-text">
          <motion.h1 variants={fadeUp}>
            Get Help. Get Better.<br />You deserve to be happy.
          </motion.h1>

          <motion.p variants={fadeUp} transition={{ delay: 0.2 }}>
            Whatever you're struggling with, get the support you need, right here.
          </motion.p>

          <motion.div
            className="hero-buttons"
            variants={fadeUp}
            transition={{ delay: 0.4 }}
          >
            <button onClick={handleBookNowClick} className="btn-primary">
              Book Now
            </button>

            <button
              onClick={() => openRegisterModal("therapist")}
              className="btn-secondary"
            >
              Register as Therapist
            </button>
          </motion.div>
        </div>

        <motion.div
          className="hero-image-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 1 } }}
        >
          <motion.img
            src={require("./assets/Subtract.webp")}
            alt="Hero Visual"
            className="hero-background-image"
            animate={{ y: [0, -18, 0] }}
            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.section>

      {/* SLOGAN */}
      <motion.p
        className="slogan-line pb-4"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        Your mental wellness journey starts with one step — let’s take it together.
      </motion.p>

      {/* SECTIONS */}
      <motion.div id="mission" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
        <Mission />
      </motion.div>

      <motion.div id="services" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
        <Offers />
      </motion.div>

      <motion.div id="why" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
        <Why />
      </motion.div>

      <motion.div id="feedback" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
        <Feedback />
      </motion.div>

      <div id="contact"></div>

      <Footer />

      {/* MODALS */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
        onSwitchToRegister={switchToRegister}
      />

      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={closeRegisterModal}
        onSwitchToLogin={switchToLogin}
        initialRole={initialRole}
      />
    </div>
  );
};

export default HomePage;
